import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/utils/api';
import { parseContinueReadingList } from '../collection-helpers';
import { Trash2, Play } from 'lucide-react';
import Pagination from '@/components/ui/Pagination';
import ColecaoObraCard from './ColecaoObraCard';

interface Obra {
    id: number;
    nome: string;
    imagem?: string;
    formato?: { id: number; nome: string; };
    total_capitulos: number;
    total_lidos?: number;
    ultimo_cap_numero?: string;
    ultimo_lido?: any;
    [key: string]: any;
}

interface ContinueReadingTabProps {
    currentPage?: number;
    itemsPerPage?: number;
}

export default function ContinueReadingTab({ currentPage = 1, itemsPerPage = 20 }: ContinueReadingTabProps) {
    const { isAuthenticated } = useAuth();
    const [searchParams] = useSearchParams();
    const formatoId = useMemo(() => {
        const f = searchParams.get('formato');
        if (f) {
            const n = Number(f);
            if (!Number.isNaN(n) && Number.isInteger(n) && n > 0) return n;
        }
        return 5;
    }, [searchParams]);

    const page = useMemo(() => {
        const p = Number(currentPage);
        return Number.isFinite(p) && p >= 1 ? Math.floor(p) : 1;
    }, [currentPage]);

    const [works, setWorks] = useState<Obra[]>([]);
    const [loading, setLoading] = useState(false);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);

    const fetchContinueReading = async (pagina: number) => {
        setLoading(true);
        try {
            const qs = new URLSearchParams({
                limit: String(itemsPerPage),
                formato: String(formatoId),
                pagina: String(pagina),
            });
            const res = await api.get(`readings/continue?${qs.toString()}`);
            const { items, totalPages, totalItems } = parseContinueReadingList<Obra>(res);
            setWorks(items);
            setTotalItems(totalItems);
            setTotalPages(totalPages);
        } catch (error) { setWorks([]); } finally { setLoading(false); }
    };

    useEffect(() => { fetchContinueReading(page); }, [page, formatoId, itemsPerPage]);

    const handleRemoveReading = async (e: React.MouseEvent, workId: number) => {
        e.preventDefault(); e.stopPropagation();
        if (!isAuthenticated) return;
        if (!window.confirm('Ocultar esta obra do Continue lendo? (O histórico de capítulos lidos não é apagado.)')) return;
        try {
            await api.patch(`readings/hide/${workId}`, {});
            setWorks((prev) => prev.filter(w => w.id !== workId));
            setTotalItems((prev) => Math.max(0, prev - 1));
        } catch (error) { alert('Não foi possível remover do Continue lendo.'); }
    };

    if (loading && works.length === 0) return <div className="flex flex-col items-center py-16 text-white"><div className="w-10 h-10 border-3 border-orange-500/30 border-t-orange-500 rounded-full animate-spin mb-4" /><p className="text-gray-300">Carregando obras...</p></div>;
    if (works.length === 0) return (
        <div className="flex flex-col items-center justify-center py-20 rounded-2xl border border-white/10 bg-white/[0.03]">
            <Play className="w-20 h-20 text-white/20 mb-4" />
            <p className="text-white/50 text-lg">Nenhuma obra para continuar lendo.</p>
        </div>
    );

    return (
        <div className="animate-in fade-in duration-500">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-6 px-0.5">
                <p className="text-sm text-white/55">
                    <span className="text-white font-bold tabular-nums">{totalItems}</span>{' '}
                    {totalItems === 1 ? 'obra' : 'obras'} no Continue lendo
                </p>
                {totalPages > 1 ? (
                    <p className="text-xs text-white/40">
                        Página {page} de {totalPages}
                    </p>
                ) : null}
            </div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 mb-8">
                {works.map((work) => (
                    <div key={work.id} className="rounded-xl border border-white/10 bg-white/[0.02] p-2 hover:border-orange-500/25 transition-colors">
                        <ColecaoObraCard
                            context="continue"
                            work={work}
                            overlayStart={
                                <button
                                    type="button"
                                    onClick={(e) => handleRemoveReading(e, work.id)}
                                    className="p-2 rounded-full bg-black/50 backdrop-blur-md border border-white/10 text-white/60 hover:text-red-400 hover:bg-black/70 transition-all md:opacity-0 md:group-hover:opacity-100 shadow-md"
                                    title="Ocultar do Continue lendo"
                                >
                                    <Trash2 size={16} />
                                </button>
                            }
                            overlayEnd={
                                work.formato?.nome ? (
                                    <span className="px-2 py-0.5 bg-black/55 backdrop-blur-md rounded text-[10px] font-semibold uppercase tracking-wider text-white/90 border border-white/10">
                                        {work.formato.nome}
                                    </span>
                                ) : null
                            }
                        />
                    </div>
                ))}
            </div>
            <Pagination
                currentPage={page}
                totalPages={totalPages}
                baseUrl="/colecao"
                extraParams={{ aba: 'continue-reading', formato: formatoId }}
            />
        </div>
    );
}
